import { type StyleProp, StyleSheet, type ViewStyle } from "react-native";

import { type ReviewFragment, isWeb, match, useToday } from "@stepapp/core";
import { differenceInDays, format, startOfDay } from "date-fns";
import React from "react";
import { profilePlaceholder } from "../assets/images";
import { palette, spacing } from "../styles";
import Avatar from "./Avatar";
import Block from "./Block";
import Button from "./Button";
import RatingScale from "./RatingScale";
import Typography from "./Typography";

type Props = {
  review: ReviewFragment;
  containerStyle?: StyleProp<ViewStyle>;
  isWorkerReview?: boolean;
  truncateComment?: boolean;
  postedAt?: string;
  countryCode?: string;
  openReviews?: () => void;
  t: NotWorth;
};

const TestimonialItem = ({
  review,
  containerStyle,
  isWorkerReview = false,
  truncateComment = true,
  postedAt,
  countryCode,
  openReviews,
  t,
}: Props) => {
  const today2 = useToday();
  const dayDiff = differenceInDays(today2, startOfDay(postedAt || "")) || 0;

  const reviewPostedAt = match(dayDiff)
    .with(0, () => t("components.footer.today"))
    .with(1, () => t("components.footer.yesterday"))
    .otherwise(() => `${dayDiff} ${t("components.footer.daysAgo")}`);

  const showDate = match(countryCode)
    .with("PL", () => true)
    .with("NO", () => false)
    .otherwise(() => true);

  const shouldTruncateComment =
    truncateComment && isWeb && !isWorkerReview && review?.comment?.length > 110;

  const comment = shouldTruncateComment ? `${review?.comment.slice(0, 110)}...` : review?.comment;

  return (
    <Block style={containerStyle}>
      <Block row marginBottom={1}>
        <Avatar size={40} uri={(review?.customerProfilePhoto || profilePlaceholder) as string} />
        <Block flex={1} marginLeft={1.75}>
          <Block marginVertical={0.125} row justifySpace>
            <Typography variant="bodyBold">{review?.customerName}</Typography>
            {isWorkerReview && (
              <RatingScale currentRating={review?.rating} padding={0} size={18} width={2.8} />
            )}
          </Block>
          <Block row justifySpace>
            <Typography type="secondary" variant="bodyLight">
              {isWorkerReview ? review?.title : review?.reservationCity}
            </Typography>
            {isWorkerReview && (
              <Typography style={{ textAlign: "right" }} type="secondary" variant="bodyLight">
                {format(review?.reservationStartedAt, "dd.MM.yyyy")}
              </Typography>
            )}
          </Block>
        </Block>
      </Block>

      {!isWorkerReview && (
        <Block paddingTop={0.5} paddingBottom={1} marginLeft={-0.5}>
          <RatingScale currentRating={review?.rating} padding={0} size={18} width={2.8} />
        </Block>
      )}

      <Typography variant="paragraph" style={styles.comment}>
        {comment}
        {shouldTruncateComment && openReviews && (
          <Button
            type="link"
            label={t("screens.dashboard.seeMore")}
            onPress={openReviews}
            textStyle={{ color: palette.common.black }}
            style={styles.linkButton}
          />
        )}
      </Typography>

      {postedAt && showDate && (
        <Block paddingTop={1}>
          <Typography type="secondary" variant="smallLabel">
            {reviewPostedAt}
          </Typography>
        </Block>
      )}
    </Block>
  );
};

export default React.memo(TestimonialItem);

const styles = StyleSheet.create({
  linkButton: {
    paddingLeft: 4,
  },
  comment: {
    minHeight: spacing.unit * 5,
  },
});
